import type {
  AnswerOption,
  OrgType,
  QuestionId,
  QuestionnaireAnswers,
  Role,
  SellsLtcInsurance,
} from './types';

export interface AnswersContext {
  answers: QuestionnaireAnswers;
  orgType: OrgType | null;
  organizationName: string | null;
  // Edward Jones pilot advisors (edjPilotAdvisor flag): the generic BGA/IMO flow is hidden for
  // them entirely — see isBgaFlowWithheld callers below.
  isEdjPilotAdvisor?: boolean;
  /**
   * Set when the advisor's partner program withholds the `distribution-partners` capability.
   * Hides the same BGA/IMO flow the Edward Jones pilot hides, so a program that withholds
   * partner connections is never asked during onboarding whether it wants one.
   */
  isDistributionPartnersRestricted?: boolean;
}

type Resolvable<T> = T | ((ctx: AnswersContext) => T);

interface BaseQuestion {
  id: QuestionId;
  prompt: Resolvable<string>;
  helperText?: Resolvable<string | undefined>;
  required: boolean;
  showIf?: (ctx: AnswersContext) => boolean;
  inferDefault?: (ctx: AnswersContext) => unknown;
}

export interface SingleSelectQuestion extends BaseQuestion {
  type: 'single-select';
  options: Resolvable<AnswerOption[]>;
}

export interface MultiSelectQuestion extends BaseQuestion {
  type: 'multi-select';
  options: Resolvable<AnswerOption[]>;
  minSelections: number;
}

export interface MultiSelectWithPrimaryQuestion extends BaseQuestion {
  type: 'multi-select-with-primary';
  options: Resolvable<AnswerOption[]>;
  primaryFieldId: QuestionId;
  secondaryFieldId: QuestionId;
  allowOther: boolean;
}

export interface ShortTextQuestion extends BaseQuestion {
  type: 'short-text';
  placeholder?: string;
}

export interface ParagraphQuestion extends BaseQuestion {
  type: 'paragraph';
  placeholder?: string;
}

export type Question =
  | SingleSelectQuestion
  | MultiSelectQuestion
  | MultiSelectWithPrimaryQuestion
  | ShortTextQuestion
  | ParagraphQuestion;

const Q3_ROLE_INFERENCE: Partial<Record<OrgType, Role>> = {
  'insurance-agency': 'insurance-agent',
  'bga-or-imo': 'insurance-agent',
  'estate-planning-firm': 'advisor-planner',
  'ccrc-or-ccah': 'operations-admin',
  'senior-living-or-home-care': 'operations-admin',
  'employee-benefits-firm': 'advisor-planner',
};

const Q3_VISIBLE_ORG_TYPES: OrgType[] = ['wealth-management-firm', 'broker-dealer', 'other'];

const Q6_VISIBLE_ORG_TYPES: OrgType[] = ['wealth-management-firm', 'broker-dealer'];

const Q8_SELLS_LTC_TRIGGERS: SellsLtcInsurance[] = [
  'yes-often',
  'yes-when-makes-sense',
  'not-yet-want-to',
];

/**
 * Whether the advisor's program keeps them out of the BGA/IMO flow altogether, regardless of
 * what their org type and LTC answers would otherwise qualify them for.
 */
function isBgaFlowWithheld(ctx: AnswersContext): boolean {
  return !!ctx.isEdjPilotAdvisor || !!ctx.isDistributionPartnersRestricted;
}

function isBgaFlowVisible({ answers, orgType }: AnswersContext): boolean {
  if (answers.user_intent === 'evaluating') return false;

  if (!orgType) return false;

  if (orgType === 'insurance-agency') return true;

  return (
    Q6_VISIBLE_ORG_TYPES.includes(orgType) &&
    answers.sells_ltc_insurance != null &&
    Q8_SELLS_LTC_TRIGGERS.includes(answers.sells_ltc_insurance)
  );
}

const TECH_STACK_VARIANT_OPTIONS: Record<'A' | 'B' | 'C' | 'D' | 'E' | 'F', AnswerOption[]> = {
  A: [
    { value: 'redtail', label: 'Redtail' },
    { value: 'wealthbox', label: 'Wealthbox' },
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'emoney', label: 'eMoney' },
    { value: 'rightcapital', label: 'RightCapital' },
    { value: 'moneyguidepro', label: 'MoneyGuidePro' },
    { value: 'holistiplan', label: 'Holistiplan' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
  B: [
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'agencybloc', label: 'AgencyBloc' },
    { value: 'radius', label: 'Radius (AgencyBloc)' },
    { value: 'smartoffice', label: 'SmartOffice (Ebix)' },
    { value: 'winflex', label: 'Winflex' },
    { value: 'ipipeline', label: 'iPipeline' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
  C: [
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'clio', label: 'Clio' },
    { value: 'mycase', label: 'MyCase' },
    { value: 'practicepanther', label: 'PracticePanther' },
    { value: 'wealthcounsel', label: 'WealthCounsel' },
    { value: 'eldercounsel', label: 'ElderCounsel' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
  D: [
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'matrixcare', label: 'MatrixCare' },
    { value: 'pointclickcare', label: 'PointClickCare' },
    { value: 'yardi', label: 'Yardi Senior Living' },
    { value: 'eldermark', label: 'Eldermark' },
    { value: 'enquire-crm', label: 'Enquire CRM' },
    { value: 'continuum-crm', label: 'Continuum CRM' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
  E: [
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'benefitpoint', label: 'BenefitPoint (Vertafore)' },
    { value: 'zywave', label: 'Zywave' },
    { value: 'employee-navigator', label: 'Employee Navigator' },
    { value: 'ease', label: 'Ease' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
  F: [
    { value: 'salesforce', label: 'Salesforce' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'other', label: 'Other' },
    { value: 'none', label: 'None of these' },
  ],
};

function techStackVariantFor(orgType: OrgType | null): 'A' | 'B' | 'C' | 'D' | 'E' | 'F' {
  switch (orgType) {
    case 'wealth-management-firm':
    case 'broker-dealer':
      return 'A';
    case 'insurance-agency':
    case 'bga-or-imo':
      return 'B';
    case 'estate-planning-firm':
      return 'C';
    case 'ccrc-or-ccah':
    case 'senior-living-or-home-care':
      return 'D';
    case 'employee-benefits-firm':
      return 'E';
    case 'other':
    case null:
    default:
      return 'F';
  }
}

const DEFAULT_PRIMARY_GOALS: AnswerOption[] = [
  { value: 'personalized-ltc-projections', label: 'Build personalized LTC cost projections' },
  { value: 'compare-funding-options', label: 'Compare self-funding vs. insurance' },
  { value: 'find-insurance-quotes', label: 'Find optimal insurance quotes' },
  { value: 'review-existing-coverage', label: 'Review or explain existing LTC coverage' },
  { value: 'family-in-planning', label: 'Bring family members into the planning conversation' },
  { value: 'standardize-workflow', label: 'Standardize our LTC planning workflow' },
  { value: 'other', label: 'Other' },
];

const CCRC_GOALS: AnswerOption[] = [
  { value: 'sell-type-a', label: 'Help sell a Type A (Life Care) contract' },
  { value: 'sell-type-c', label: 'Help sell a Type C (Fee-for-Service) contract' },
  { value: 'understand-ltc-costs', label: 'Help residents/families understand LTC costs' },
  {
    value: 'support-move-in-conversations',
    label: 'Support move-in conversations with cost projections',
  },
  { value: 'educate-funding-options', label: 'Educate families on funding options' },
  { value: 'other', label: 'Other' },
];

const SENIOR_LIVING_GOALS: AnswerOption[] = [
  { value: 'understand-ltc-costs', label: 'Help residents/families understand LTC costs' },
  {
    value: 'support-move-in-conversations',
    label: 'Support move-in conversations with cost projections',
  },
  { value: 'educate-funding-options', label: 'Educate families on funding options' },
  { value: 'other', label: 'Other' },
];

const EVALUATOR_GOAL: AnswerOption = {
  value: 'rollout-across-team',
  label: 'Roll Waterlily out across my team',
};

function primaryGoalOptionsFor(ctx: AnswersContext): AnswerOption[] {
  const { answers, orgType } = ctx;

  if (orgType === 'ccrc-or-ccah') return CCRC_GOALS;
  if (orgType === 'senior-living-or-home-care') return SENIOR_LIVING_GOALS;

  const moveToFront = (list: AnswerOption[], value: string): AnswerOption[] => {
    const idx = list.findIndex(opt => opt.value === value);

    if (idx <= 0) return list;

    const copy = list.slice();
    const [item] = copy.splice(idx, 1);
    copy.unshift(item);

    return copy;
  };

  let options = DEFAULT_PRIMARY_GOALS.slice();

  if (answers.user_intent === 'evaluating') {
    options = moveToFront(options, 'standardize-workflow');
    options.splice(1, 0, EVALUATOR_GOAL);
  }

  return options;
}

const DEFAULT_LTC_CHALLENGES: AnswerOption[] = [
  { value: 'clients-shrug-off-risk', label: 'Clients shrug off the risk' },
  { value: 'feels-like-sales-pitch', label: 'It feels like a sales pitch' },
  { value: 'cost-uncertainty', label: 'Lack of certainty around costs' },
  { value: 'underwriting-uncertainty', label: 'Underwriting uncertainty' },
  { value: 'other', label: 'Other' },
];

const INSURANCE_CHALLENGE_PREPENDS: AnswerOption[] = [
  { value: 'clients-want-self-fund', label: 'Clients want to self-fund' },
  { value: 'clients-not-insurable', label: "Clients aren't insurable" },
];

function ltcChallengesOptionsFor(ctx: AnswersContext): AnswerOption[] {
  const isInsuranceLeaning =
    ctx.orgType === 'insurance-agency' || ctx.answers.sells_ltc_insurance === 'yes-often';

  return isInsuranceLeaning
    ? [...INSURANCE_CHALLENGE_PREPENDS, ...DEFAULT_LTC_CHALLENGES]
    : DEFAULT_LTC_CHALLENGES;
}

export const QUESTIONS: Question[] = [
  {
    id: 'user_intent',
    type: 'single-select',
    prompt: 'Are you setting Waterlily up for yourself, or evaluating it for a team?',
    required: true,
    options: [
      { value: 'just-for-me', label: 'Just for me' },
      { value: 'small-team', label: 'For myself and a small team' },
      { value: 'evaluating', label: 'Evaluating for my organization' },
    ],
  },
  {
    id: 'role',
    type: 'single-select',
    prompt: 'What best describes your role?',
    required: true,
    showIf: ({ answers, orgType }) =>
      answers.user_intent !== 'evaluating' && !!orgType && Q3_VISIBLE_ORG_TYPES.includes(orgType),
    inferDefault: ({ orgType }) => (orgType ? Q3_ROLE_INFERENCE[orgType] : undefined),
    options: [
      { value: 'advisor-planner', label: 'Advisor / Planner' },
      { value: 'insurance-agent', label: 'Insurance Agent' },
      { value: 'owner-principal', label: 'Owner / Principal' },
      { value: 'paraplanner-associate', label: 'Paraplanner / Associate' },
      { value: 'operations-admin', label: 'Operations / Admin' },
      { value: 'other', label: 'Other' },
    ],
  },
  {
    id: 'ltc_volume',
    type: 'single-select',
    prompt: 'Do you already support clients with long-term care planning today?',
    required: true,
    showIf: ({ answers }) => answers.user_intent !== 'evaluating',
    options: [
      { value: 'all-the-time', label: 'All the time! (9+ plans per year)' },
      { value: 'occasionally', label: 'Occasionally (3-8 plans per year)' },
      { value: 'rarely', label: 'Rarely (1-2 plans per year)' },
      { value: 'not-yet', label: 'Not yet' },
    ],
  },
  {
    id: 'ltc_initiator',
    type: 'single-select',
    prompt:
      'When you start a long-term care plan, is it because your client asked to, or do you bring it up proactively?',
    required: true,
    showIf: ({ answers }) =>
      answers.user_intent !== 'evaluating' &&
      answers.ltc_volume != null &&
      answers.ltc_volume !== 'not-yet',
    options: [
      { value: 'clients-bring-it-up', label: 'Usually my clients bring it up' },
      { value: 'even-mix', label: 'Even mix of both' },
      { value: 'i-bring-it-up', label: 'Usually I bring it up' },
    ],
  },
  {
    id: 'sells_ltc_insurance',
    type: 'single-select',
    prompt: 'Do you sell long-term care insurance?',
    required: true,
    showIf: ({ answers, orgType }) =>
      answers.user_intent !== 'evaluating' && !!orgType && Q6_VISIBLE_ORG_TYPES.includes(orgType),
    options: [
      { value: 'yes-often', label: "Yes, it's often part of my LTC plans" },
      { value: 'yes-when-makes-sense', label: 'I can, but only when it makes sense' },
      { value: 'partner-handles-it', label: 'I have a partner or third party who does it for me' },
      { value: 'not-yet-want-to', label: "Not yet, but I'd like to" },
      { value: 'no-and-dont-want-to', label: "No, and I don't want to" },
    ],
  },
  {
    id: 'is_licensed',
    type: 'single-select',
    prompt: 'Are you already insurance licensed?',
    required: true,
    showIf: ({ answers }) => answers.sells_ltc_insurance === 'not-yet-want-to',
    inferDefault: ({ orgType }) =>
      orgType === 'insurance-agency' ? 'yes' : 'no-and-dont-want-to-be',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'not-yet-want-to-be', label: 'Not yet, I want to be' },
      { value: 'no-and-dont-want-to-be', label: "No, and I don't want to be" },
    ],
  },
  {
    id: 'bga_status',
    type: 'single-select',
    prompt: 'Are you already contracted with any BGAs, IMOs, or insurance carriers?',
    required: true,
    showIf: ctx => isBgaFlowVisible(ctx) && !isBgaFlowWithheld(ctx),
    options: [
      { value: 'yes-great', label: "Yes, and they're great!" },
      { value: 'yes-but-move', label: 'Yes, but I want to move' },
      { value: 'not-yet', label: 'Not yet' },
    ],
  },
  {
    id: 'current_bgas',
    type: 'short-text',
    prompt:
      'Who is your current BGA / IMO? If you use multiple, please list them all, separated by commas.',
    required: true,
    showIf: ctx =>
      !isBgaFlowWithheld(ctx) &&
      (ctx.answers.bga_status === 'yes-great' || ctx.answers.bga_status === 'yes-but-move'),
    placeholder: 'e.g., LTC Global, ACSIA Partners',
  },
  {
    id: 'bga_partner_interest',
    type: 'single-select',
    prompt:
      'Would you like to connect with our BGA partners or get contracted under our subsidiary?',
    helperText:
      'Waterlily works with several BGAs and IMOs who offer full-service back office and marketing support who we can connect you with. If you want something without the frills, our subsidiary can get you contracted with Nationwide, OneAmerica, Securian Financial, Lincoln Financial, CareScout, and EquiTrust.',
    required: true,
    showIf: ctx =>
      isBgaFlowVisible(ctx) &&
      !isBgaFlowWithheld(ctx) &&
      ctx.answers.bga_status !== 'yes-great',
    options: [
      {
        value: 'connect-with-partners',
        label: "I'd like to connect with Waterlily's partner BGAs/IMOs",
      },
      {
        value: 'explore-waterlily-bga',
        label: "I'd like to explore getting contracted under Waterlily's BGA",
      },
      { value: 'all-set-for-now', label: "I'm all set for now, thanks" },
    ],
  },
  {
    id: 'tech_stack',
    type: 'multi-select',
    prompt: 'What CRM or planning tools do you use today? (Select all that apply)',
    required: true,
    minSelections: 1,
    showIf: ({ answers }) => answers.user_intent !== 'evaluating',
    options: ({ orgType }) => TECH_STACK_VARIANT_OPTIONS[techStackVariantFor(orgType)],
  },
  {
    id: 'primary_goal',
    type: 'multi-select-with-primary',
    prompt: 'What are you hoping to accomplish with Waterlily first?',
    helperText: 'Pick your top goal, then any others that apply.',
    required: true,
    primaryFieldId: 'primary_goal',
    secondaryFieldId: 'secondary_goals',
    allowOther: true,
    options: primaryGoalOptionsFor,
  },
  {
    id: 'ltc_challenges',
    type: 'multi-select',
    prompt:
      'What are your biggest challenges with long-term care planning today? (Select all that apply)',
    required: true,
    minSelections: 1,
    showIf: ({ answers }) => answers.user_intent !== 'evaluating',
    options: ltcChallengesOptionsFor,
  },
  {
    id: 'process_description',
    type: 'paragraph',
    prompt:
      'In your own words, what does your long-term care planning process look like today?\n\nWhat do you like about it, and what do you dislike?',
    helperText: 'Optional, but the more you share, the more tailored your recommendation will be.',
    required: false,
    showIf: ({ answers }) => answers.user_intent !== 'evaluating',
  },
];

export const QUESTIONS_BY_ID: Record<QuestionId, Question> = QUESTIONS.reduce(
  (acc, q) => {
    acc[q.id] = q;
    return acc;
  },
  {} as Record<QuestionId, Question>,
);

export function resolveOptions(question: Question, ctx: AnswersContext): AnswerOption[] {
  if ('options' in question && question.options != null) {
    return typeof question.options === 'function' ? question.options(ctx) : question.options;
  }

  return [];
}

export function resolvePrompt(question: Question, ctx: AnswersContext): string {
  return typeof question.prompt === 'function' ? question.prompt(ctx) : question.prompt;
}

export function resolveHelperText(question: Question, ctx: AnswersContext): string | undefined {
  if (question.helperText == null) return undefined;

  return typeof question.helperText === 'function' ? question.helperText(ctx) : question.helperText;
}
